/*
  BizTracker System Builder controller: the DOM half. Every rule lives in
  biztracker-system-builder-engine.js, which the tests load as it ships; this file
  renders questions, keeps answers, shows the engine's result and hands it to sales.

  It never decides anything the engine did not: no model is named unless the engine
  returned one, no slot is dropped, no processor is picked for the shopper.

  Privacy, by construction:
    - builder state holds answers about the store, never who is asking;
    - answers persist in sessionStorage under the flow's own versioned key, and only
      there — no localStorage, no cookie — and "Start over" clears that key alone;
    - analytics are DOM events whose detail passes engine.safeEventDetail, an
      allow-list of ids and counts; nothing is pushed to a tag manager from here;
    - the contact fields are never read by this file. Identity travels in the form's
      own fields when the shopper sends it, and nowhere else.

  THE LEAD HOOK (round 2, for the HubSpot P0 lane). There is one lead payload, the one
  data/system-builder/lead-handoff-contract.json defines, built by engine.leadPayload() and
  nowhere else. This file offers it two ways, both carrying exactly that payload:
    - window.BizTrackerSystemBuilder.leadSubmission(node?) returns
      { lead_contract_version, payload, summary } for the Builder containing node (or the
      first on the page), or null before the Builder has loaded;
    - on send, the root dispatches 'biztracker:builder_lead' (bubbles, cancelable) with the
      same object as detail, after filling the hidden contact fields with it. A listener that
      calls preventDefault() takes over delivery and the Shopify form is not sent: it then
      owns the submission, including falling back to the Shopify form if its own fails.
  Neither carries identity: the adapter reads name, email and phone from the form's own
  fields at submit time, as the Shopify form does.
*/
(function () {
  'use strict';

  var engine = window.BizTrackerSystemBuilderEngine;
  var instances = [];

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function setUp(root) {
    var statusNode = root.querySelector('[data-bt-builder-status]');
    var form = root.querySelector('[data-bt-builder-form]');
    var summaryNode = root.querySelector('[data-bt-summary]');
    var backButton = root.querySelector('[data-bt-back]');
    var nextButton = root.querySelector('[data-bt-next]');
    var resetButton = root.querySelector('[data-bt-reset]');
    var progressNode = root.querySelector('[data-bt-progress]');
    var progressLabel = root.querySelector('[data-bt-progress-label]');
    var progressFill = root.querySelector('[data-bt-progress-fill]');
    var fieldsets = Array.prototype.slice.call(root.querySelectorAll('[data-bt-step]'));
    var handoff = root.querySelector('[data-bt-handoff]');
    var summaryField = root.querySelector('[data-bt-summary-field]');
    var payloadField = root.querySelector('[data-bt-payload-field]');
    var contactForm = handoff ? handoff.querySelector('form') : null;
    var handoffSuccess = root.querySelector('[data-bt-handoff-success]');

    var payload = null;
    var flow = null;
    var state = null;
    var stepIndex = 0;
    var preset = null;
    var started = false;
    var summaryViewed = false;

    function fail() {
      statusNode.textContent = 'The System Builder could not load. You can still request a POS business review or contact BizTracker.';
      form.hidden = true;
      if (progressNode) progressNode.hidden = true;
    }

    if (!engine) { fail(); return; }

    // A restaurant takes the LINGA path after the first answer: the retail questions
    // do not describe its counter, so a person scopes it instead.
    function stepIds() {
      var type = null;
      for (var i = 0; i < flow.businessTypes.length; i += 1) {
        if (flow.businessTypes[i].id === state.businessType) type = flow.businessTypes[i];
      }
      if (type && type.path === 'restaurant') return ['business_type', 'summary'];
      return flow.steps.map(function (s) { return s.id; });
    }

    function currentStepId() {
      var ids = stepIds();
      return ids[Math.min(stepIndex, ids.length - 1)];
    }

    function storageKey() { return flow.stateStorageKey; }

    function persist() {
      try {
        window.sessionStorage.setItem(storageKey(), JSON.stringify({
          businessType: state.businessType,
          storeNeeds: state.storeNeeds,
          checkoutStations: state.checkoutStations,
          counterNeeds: state.counterNeeds,
          delivery: state.delivery,
          additions: state.additions,
          processing: state.processing,
          tier: state.tier,
          step: stepIndex
        }));
      } catch (error) { /* storage unavailable: the builder still works */ }
    }

    function readStored() {
      try {
        var raw = window.sessionStorage.getItem(storageKey());
        return raw ? JSON.parse(raw) : null;
      } catch (error) { return null; }
    }

    function clearStored() {
      try { window.sessionStorage.removeItem(storageKey()); } catch (error) { /* nothing to clear */ }
    }

    function emit(name, detail) {
      root.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: engine.safeEventDetail(detail, payload) }));
    }

    // Where the shopper came from: a same-origin referrer's path, never its query, and never
    // another site's address. The engine allow-lists it again.
    function referrerPath() {
      var ref = typeof document.referrer === 'string' ? document.referrer : '';
      var match = /^[a-z][a-z0-9+.\-]*:\/\/([^\/?#]+)(\/[^?#]*)?/i.exec(ref);
      if (!match || !window.location || match[1] !== window.location.host) return null;
      return match[2] || '/';
    }

    // The campaign the page already holds. window.btAttribution exists only when the visitor
    // allowed analytics and marketing (biztracker-attribution.liquid); this file never reads
    // the URL for campaign data itself.
    function leadMeta() {
      var held = window.btAttribution;
      return { preset: preset, originatingPage: referrerPath(), attribution: held && typeof held === 'object' ? held : null };
    }

    function currentLead() {
      if (!payload || !state) return null;
      var result = engine.derive(state, payload);
      return {
        lead_contract_version: engine.LEAD_CONTRACT_VERSION,
        payload: engine.leadPayload(result, state, leadMeta()),
        summary: engine.summaryText(result, payload)
      };
    }
    instances.push({ root: root, lead: currentLead });

    function baseDetail(extra) {
      var result = engine.derive(state, payload);
      var detail = {
        builder_version: payload.version,
        business_type: state.businessType,
        path: result.path,
        checkout_stations: state.checkoutStations,
        processing_interest: state.processing,
        tier: result.startingConfiguration ? result.startingConfiguration.tier.id : null,
        verification_required_count: result.verificationRequiredCount,
        preset_used: preset !== null
      };
      for (var key in extra) if (Object.prototype.hasOwnProperty.call(extra, key)) detail[key] = extra[key];
      return detail;
    }

    function markStarted() {
      if (started) return;
      started = true;
      emit('biztracker:builder_started', { builder_version: payload.version, preset_used: preset !== null });
    }

    // Each control is named by its label explicitly as well as by containment, so every
    // assistive technology reads the answer's words rather than its id.
    var controlCount = 0;
    function choice(type, name, value, label, checked, onChange) {
      var wrapper = el('label', 'bt-system-builder__choice');
      var input = document.createElement('input');
      controlCount += 1;
      input.id = root.id + '-control-' + controlCount;
      wrapper.htmlFor = input.id;
      input.type = type;
      input.name = name;
      input.value = value;
      input.checked = checked;
      input.addEventListener('change', function () {
        markStarted();
        onChange(input.checked);
        persist();
      });
      wrapper.appendChild(input);
      wrapper.appendChild(el('span', 'bt-system-builder__choice-label', label));
      return wrapper;
    }

    function flagChoices(host, list, target, name) {
      list.forEach(function (item) {
        host.appendChild(choice('checkbox', name, item.id, item.label, target[item.id] === true, function (on) {
          target[item.id] = on;
        }));
      });
    }

    var RENDER = {
      business_type: function (host) {
        flow.businessTypes.forEach(function (item) {
          host.appendChild(choice('radio', 'bt-business-type', item.id, item.label, state.businessType === item.id, function () {
            state.businessType = item.id;
          }));
        });
      },
      store_needs: function (host) { flagChoices(host, flow.storeNeeds, state.storeNeeds, 'bt-store-needs'); },
      checkout_stations: function (host) {
        var wrapper = el('label', 'bt-system-builder__stations');
        wrapper.appendChild(el('span', null, 'How many checkout stations will the store run?'));
        var input = document.createElement('input');
        input.id = root.id + '-stations';
        wrapper.htmlFor = input.id;
        input.type = 'number';
        input.min = '1';
        input.max = String(engine.MAX_STATIONS);
        input.inputMode = 'numeric';
        input.value = String(state.checkoutStations);
        input.addEventListener('input', markStarted);
        // Normalised on blur, not per keystroke, so "10" is not rewritten to "1" mid-entry.
        input.addEventListener('blur', function () {
          state.checkoutStations = engine.normalizeState({ checkoutStations: Number(input.value) }, payload).checkoutStations;
          input.value = String(state.checkoutStations);
          persist();
        });
        wrapper.appendChild(input);
        host.appendChild(wrapper);
      },
      counter: function (host) { flagChoices(host, flow.counterNeeds, state.counterNeeds, 'bt-counter'); },
      services: function (host) {
        host.appendChild(el('p', 'bt-system-builder__note', 'Implementation and training are part of every system. Choose how:'));
        flow.services.delivery.forEach(function (item) {
          host.appendChild(choice('radio', 'bt-delivery', item.id, item.label, state.delivery === item.id, function () {
            state.delivery = item.id;
          }));
        });
        host.appendChild(el('p', 'bt-system-builder__note', 'And anything else the project needs:'));
        flow.services.additions.forEach(function (item) {
          host.appendChild(choice('checkbox', 'bt-additions', item.id, item.label, state.additions.indexOf(item.id) !== -1, function (on) {
            var at = state.additions.indexOf(item.id);
            if (on && at === -1) state.additions.push(item.id);
            if (!on && at !== -1) state.additions.splice(at, 1);
          }));
        });
      },
      processing: function (host) {
        host.appendChild(el('p', 'bt-system-builder__note', flow.processing.intro));
        flow.processing.choices.forEach(function (item) {
          host.appendChild(choice('radio', 'bt-processing', item.id, item.label, state.processing === item.id, function () {
            state.processing = item.id;
          }));
        });
      },
      summary: function (host) {
        var result = engine.derive(state, payload);
        if (result.startingConfiguration) {
          host.appendChild(el('p', 'bt-system-builder__note', 'Starting configurations, lowest to highest. Compare them with sales:'));
          payload.ladder.tiers.forEach(function (tier) {
            host.appendChild(choice('radio', 'bt-tier', tier.id, tier.name, state.tier === tier.id, function () {
              state.tier = tier.id;
              renderSummary();
            }));
          });
        }
        renderSummary();
      }
    };

    function slotRow(slot) {
      var row = el('li', 'bt-system-builder__slot');
      var qty = slot.quantity == null ? 'per store, scoped with sales' : '× ' + slot.quantity;
      row.appendChild(el('span', 'bt-system-builder__slot-name', slot.label + ' (' + qty + ')'));
      // A slot with no verified model shows its label and quantity only: no verification
      // badge (Kris, 2026-09-28). Its state still reaches sales in the lead payload.
      if (slot.model) row.appendChild(el('span', 'bt-system-builder__model', slot.model));
      if (slot.review) row.appendChild(el('span', 'bt-system-builder__slot-note', slot.review));
      return row;
    }

    function renderSummary() {
      var result = engine.derive(state, payload);
      summaryNode.innerHTML = '';
      summaryNode.hidden = false;

      summaryNode.appendChild(el('h3', 'bt-system-builder__summary-heading',
        (result.businessType ? result.businessType.label : 'Your store') + ' — ' + (result.platform || 'platform to be decided')));

      if (result.startingConfiguration) {
        summaryNode.appendChild(el('p', null, 'Checkout stations: ' + result.checkoutStations));
        summaryNode.appendChild(el('p', 'bt-system-builder__tier', 'Starting configuration: ' + result.startingConfiguration.tier.name));
        // A software plan is named only by a label Kris supplied and approved; without one the
        // plan slot is internal and nothing is shown.
        if (result.softwarePlan && result.softwarePlan.label) summaryNode.appendChild(el('p', 'bt-system-builder__tier', result.softwarePlan.label));
        var list = el('ul', 'bt-system-builder__slots');
        result.startingConfiguration.slots.forEach(function (slot) { list.appendChild(slotRow(slot)); });
        summaryNode.appendChild(list);
        if (result.additionalScope.length) {
          summaryNode.appendChild(el('p', null, 'Additional scope to review: ' + result.additionalScope.map(function (s) { return s.label; }).join(', ') + '.'));
        }
      }
      if (result.restaurantRoute) {
        summaryNode.appendChild(el('p', null, result.restaurantRoute.note));
        var link = el('a', 'bt-system-builder__route', result.restaurantRoute.label);
        link.href = result.restaurantRoute.url;
        summaryNode.appendChild(link);
      }

      if (result.services) {
        summaryNode.appendChild(el('p', null, 'Implementation: ' + result.services.delivery.label +
          (result.services.additions.length ? '; ' + result.services.additions.map(function (a) { return a.label; }).join('; ') : '') + '.'));
      }
      if (result.processing) {
        summaryNode.appendChild(el('p', null, 'Card processing: ' + result.processing.interest.label +
          (result.processing.paths.length ? ' (paths reviewed: ' + result.processing.paths.join(' and ') + ')' : '') + '.'));
      }
      // Only the notes the engine marks for the shopper; the sales-only ones travel in the lead.
      var shopperNotes = result.reviewNotes.filter(function (n) { return n.shopper === true; });
      if (shopperNotes.length) {
        var notes = el('ul', 'bt-system-builder__notes');
        shopperNotes.forEach(function (n) { notes.appendChild(el('li', null, n.text)); });
        summaryNode.appendChild(notes);
      }
      summaryNode.appendChild(el('p', 'bt-system-builder__note', flow.summaryNote));

      // The handoff is filled from the scope, never from the form, so neither field can
      // carry what the shopper types about themselves.
      var lead = currentLead();
      if (summaryField) summaryField.value = lead.summary;
      if (payloadField) payloadField.value = JSON.stringify(lead.payload);
      if (handoff) handoff.hidden = false;

      if (!summaryViewed) {
        summaryViewed = true;
        emit('biztracker:builder_summary_viewed', baseDetail({}));
      }
      persist();
    }

    function show(index) {
      var ids = stepIds();
      stepIndex = Math.max(0, Math.min(index, ids.length - 1));
      var id = ids[stepIndex];
      fieldsets.forEach(function (fieldset) {
        var mine = fieldset.getAttribute('data-bt-step') === id;
        fieldset.hidden = !mine;
        if (!mine) return;
        var host = fieldset.querySelector('[data-bt-step-fields]');
        host.innerHTML = '';
        RENDER[id](host);
      });
      var onSummary = id === 'summary';
      if (!onSummary) {
        summaryNode.hidden = true;
        if (handoff) handoff.hidden = true;
      }
      backButton.disabled = stepIndex === 0;
      nextButton.hidden = onSummary;
      nextButton.textContent = stepIndex === ids.length - 2 ? 'See my starting configuration' : 'Continue';
      progressNode.hidden = false;
      progressNode.setAttribute('aria-valuemax', String(ids.length));
      progressNode.setAttribute('aria-valuenow', String(stepIndex + 1));
      progressLabel.textContent = 'Step ' + (stepIndex + 1) + ' of ' + ids.length;
      if (progressFill) progressFill.style.width = ((stepIndex + 1) / ids.length * 100) + '%';
      persist();
    }

    function advance() {
      if (currentStepId() === 'business_type' && !state.businessType) {
        statusNode.textContent = 'Choose the kind of business to continue.';
        return;
      }
      statusNode.textContent = '';
      markStarted();
      emit('biztracker:builder_step_completed', baseDetail({ step_id: currentStepId() }));
      show(stepIndex + 1);
    }

    function start(loaded) {
      payload = loaded;
      flow = loaded.flow;
      var stored = readStored();
      state = engine.normalizeState(stored || {}, payload);
      var hinted = engine.presetFromHash(window.location.hash, payload);
      // A hint preselects the business type when the shopper has not chosen one. It
      // never overrides an answer, and it approves nothing.
      if (handoffSuccess) {
        emit('biztracker:builder_handoff_succeeded', baseDetail({}));
      }
      if (hinted && !state.businessType) {
        state.businessType = hinted;
        preset = hinted;
        if (root.scrollIntoView) root.scrollIntoView();
      }
      form.hidden = false;
      var resume = stored && typeof stored.step === 'number' && Math.floor(stored.step) === stored.step ? stored.step : 0;
      show(resume);

      nextButton.addEventListener('click', advance);
      backButton.addEventListener('click', function () { show(stepIndex - 1); });
      resetButton.addEventListener('click', function () {
        clearStored();
        state = engine.normalizeState({}, payload);
        preset = null;
        summaryViewed = false;
        statusNode.textContent = '';
        show(0);
      });
      if (contactForm) {
        // Bound to BOTH 'submit' and 'bt:lead-prepare'.
        //
        // snippets/biztracker-lead-events.liquid stops propagation of the real submit, so that
        // Shopify's hCaptcha wrapper cannot run the native submit and navigate away while the lead
        // is still being delivered. That also means this listener never sees 'submit' when the
        // snippet is present, so the snippet asks for the same work through 'bt:lead-prepare'
        // instead. Without the second binding the payload below would silently stop being written,
        // and nothing would fail loudly.
        //
        // The veto maps across unchanged: preventDefault on 'bt:lead-prepare' makes dispatchEvent
        // return false in the snippet, which aborts delivery.
        var handoffTick = false;
        var vetoed = false;
        var onHandoff = function (event) {
          // Only one of the two events arrives in practice. If an environment ignores
          // stopPropagation both would, and the shopper would get two handoff events for one
          // press - so do the work once and apply the same decision to the duplicate.
          if (handoffTick) {
            if (vetoed && event && event.preventDefault) event.preventDefault();
            return;
          }
          handoffTick = true;
          setTimeout(function () { handoffTick = false; }, 0);

          // "handoff started", not "lead created": this runs before the browser
          // navigates, so all it can honestly report is that the shopper pressed send.
          renderSummary();
          emit('biztracker:builder_handoff_started', baseDetail({}));
          // The lead hook. Not an analytics event, so it bypasses safeEventDetail; it carries
          // the contract payload, which holds no identity by construction.
          var hook = new CustomEvent('biztracker:builder_lead', { bubbles: true, cancelable: true, detail: currentLead() });
          vetoed = !root.dispatchEvent(hook);
          if (vetoed && event && event.preventDefault) event.preventDefault();
        };
        contactForm.addEventListener('submit', onHandoff);
        contactForm.addEventListener('bt:lead-prepare', onHandoff);
        // The lead-events snippet (not this controller) delivers the form to the CRM without a
        // page load and dispatches this only on the CRM's 2xx, so "succeeded" still means received.
        contactForm.addEventListener('bt:lead-delivered', function () {
          emit('biztracker:builder_handoff_succeeded', baseDetail({}));
        });
      }
    }

    var url = root.getAttribute('data-config-url');
    if (!url || !window.fetch) { fail(); return; }
    window.fetch(url, { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error('builder data ' + response.status);
        return response.json();
      })
      .then(function (loaded) {
        if (!loaded || !loaded.flow || !loaded.ladder || loaded.version !== engine.VERSION) throw new Error('builder data is not usable');
        start(loaded);
      })
      .catch(fail);
  }

  function instanceFor(node) {
    for (var i = 0; i < instances.length; i += 1) {
      if (node && instances[i].root.contains && instances[i].root.contains(node)) return instances[i];
    }
    return instances.length ? instances[0] : null;
  }

  window.BizTrackerSystemBuilder = {
    leadContractVersion: engine ? engine.LEAD_CONTRACT_VERSION : null,
    leadSubmission: function (node) {
      var found = instanceFor(node);
      return found ? found.lead() : null;
    }
  };

  function init() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-bt-system-builder]'), setUp);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
